import { useState, useMemo } from "react"
import { useOutletContext } from "react-router-dom"
import { AreaChart } from "lucide-react"
import ToolShell from "../components/ToolShell"
import FormulaBox from "../components/FormulaBox"
import ToolFAQ from "../components/ToolFAQ"
import CTABox from "../components/CTABox"
import RelatedToolsList from "../components/RelatedToolsList"
import { ToolDef } from "../toolRegistry"

export default function DepreciationCalculatorPage() {
  const { tool } = useOutletContext<{ tool: ToolDef; currency: string }>()

  const [priceStr, setPriceStr] = useState("")
  const [landStr, setLandStr] = useState("")
  const [improvementsStr, setImprovementsStr] = useState("")
  const [propertyType, setPropertyType] = useState<"residential" | "commercial">("residential")
  const [purchaseDate, setPurchaseDate] = useState("")
  const [priorDepStr, setPriorDepStr] = useState("")
  const [taxRate, setTaxRate] = useState(24)

  const results = useMemo(() => {
    const price = parseFloat(priceStr) || 0
    const land = parseFloat(landStr) || 0
    const improvements = parseFloat(improvementsStr) || 0
    const priorDep = parseFloat(priorDepStr) || 0

    if (price <= 0 || land >= price) return null

    const depreciableBasis = price - land + improvements
    const recoveryPeriod = propertyType === "residential" ? 27.5 : 39
    const annualDep = depreciableBasis / recoveryPeriod
    const annualTaxSavings = annualDep * (taxRate / 100)
    const totalDep = depreciableBasis
    const remainingBasis = Math.max(0, depreciableBasis - priorDep)

    // Mid-month convention for first year
    let firstYearDep = annualDep
    let firstYearLabel = "Year 1"
    if (purchaseDate) {
      const monthPlaced = new Date(purchaseDate).getMonth() // 0-indexed
      const firstYearMonths = 12 - monthPlaced - 0.5
      firstYearDep = (depreciableBasis / recoveryPeriod) * (firstYearMonths / 12)
      const monthName = new Date(purchaseDate).toLocaleString("default", { month: "short" })
      firstYearLabel = `Year 1 (from ${monthName}, mid-month)`
    }

    // 5-year schedule
    const schedule: { label: string; dep: number; cumulative: number; remaining: number }[] = []
    let cumulative = 0

    cumulative += firstYearDep
    schedule.push({ label: firstYearLabel, dep: firstYearDep, cumulative, remaining: Math.max(0, depreciableBasis - cumulative) })

    for (let yr = 2; yr <= 5; yr++) {
      cumulative += annualDep
      schedule.push({ label: `Year ${yr}`, dep: annualDep, cumulative, remaining: Math.max(0, depreciableBasis - cumulative) })
    }

    return {
      depreciableBasis,
      recoveryPeriod,
      annualDep,
      annualTaxSavings,
      totalDep,
      remainingBasis,
      firstYearDep,
      schedule,
    }
  }, [priceStr, landStr, improvementsStr, propertyType, purchaseDate, priorDepStr, taxRate])

  const fmt = (n: number) => `$${Math.round(n).toLocaleString()}`

  return (
    <div className="space-y-8">
      <ToolShell>
        <div className="p-6 space-y-4">
          {/* US-Only Badge */}
          <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5">
            <span className="text-lg">🇺🇸</span>
            <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">US Tax Tool — MACRS Depreciation</span>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Inputs */}
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Property Details (USD)</p>

              {([
                ["Purchase Price", priceStr, setPriceStr, "e.g. 350000"],
                ["Land Value", landStr, setLandStr, "e.g. 75000 (land cannot be depreciated)"],
                ["Improvements Made", improvementsStr, setImprovementsStr, "Optional — renovations, additions"],
              ] as const).map(([label, val, setter, ph]) => (
                <div key={label}>
                  <label className="block text-xs font-medium text-slate-500 mb-1">{label}</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                    <input
                      type="number"
                      min="0"
                      value={val}
                      onChange={(e) => (setter as (v: string) => void)(e.target.value)}
                      placeholder={ph}
                      className="w-full rounded-lg border border-slate-200 bg-white pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                  </div>
                </div>
              ))}

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Property Type</label>
                <select
                  value={propertyType}
                  onChange={(e) => setPropertyType(e.target.value as "residential" | "commercial")}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                >
                  <option value="residential">Residential (27.5-year recovery)</option>
                  <option value="commercial">Commercial (39-year recovery)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Placed-in-Service Date <span className="text-slate-400">(for mid-month convention)</span>
                </label>
                <input
                  type="date"
                  value={purchaseDate}
                  onChange={(e) => setPurchaseDate(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Prior Depreciation Claimed (Optional)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                  <input
                    type="number"
                    min="0"
                    value={priorDepStr}
                    onChange={(e) => setPriorDepStr(e.target.value)}
                    placeholder="If already claiming depreciation"
                    className="w-full rounded-lg border border-slate-200 bg-white pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Your Marginal Tax Rate: {taxRate}%
                </label>
                <input
                  type="range"
                  min="10"
                  max="37"
                  step="1"
                  value={taxRate}
                  onChange={(e) => setTaxRate(Number(e.target.value))}
                  className="w-full accent-primary"
                />
                <div className="flex justify-between text-xs text-slate-400 mt-0.5">
                  <span>10%</span><span>37%</span>
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="rounded-2xl border border-primary/20 bg-primary/5 p-5 space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Results</p>

              {!results ? (
                <p className="text-slate-400 text-sm text-center py-10">
                  Enter purchase price and land value to calculate depreciation
                </p>
              ) : (
                <>
                  <div className="text-center py-2">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Annual Depreciation Deduction</p>
                    <p className="text-5xl font-extrabold text-primary">{fmt(results.annualDep)}</p>
                    <p className="text-sm text-slate-500 mt-1">{results.recoveryPeriod}-year recovery period</p>
                  </div>

                  <div className="border-t border-primary/10 pt-3 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Depreciable Basis</span>
                      <span className="font-semibold text-slate-900">{fmt(results.depreciableBasis)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Est. Annual Tax Savings</span>
                      <span className="font-semibold text-green-600">{fmt(results.annualTaxSavings)}</span>
                    </div>
                    {purchaseDate && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">First-Year Depreciation</span>
                        <span className="font-semibold text-slate-900">{fmt(results.firstYearDep)}</span>
                      </div>
                    )}
                    {parseFloat(priorDepStr) > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Remaining Depreciable Basis</span>
                        <span className="font-semibold text-slate-900">{fmt(results.remainingBasis)}</span>
                      </div>
                    )}
                  </div>

                  {/* 5-Year Schedule */}
                  <div className="border-t border-primary/10 pt-4">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">5-Year Schedule</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-slate-500">
                            <th className="text-left pb-1 font-medium">Period</th>
                            <th className="text-right pb-1 font-medium">Deduction</th>
                            <th className="text-right pb-1 font-medium">Cumulative</th>
                            <th className="text-right pb-1 font-medium">Remaining</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {results.schedule.map((row, i) => (
                            <tr key={i} className="text-slate-700">
                              <td className="py-1.5 pr-2 text-slate-600">{row.label}</td>
                              <td className="text-right py-1.5 font-medium">{fmt(row.dep)}</td>
                              <td className="text-right py-1.5">{fmt(row.cumulative)}</td>
                              <td className="text-right py-1.5">{fmt(row.remaining)}</td>
                            </tr>
                          ))}
                          <tr className="text-xs text-slate-400">
                            <td colSpan={4} className="pt-1.5 text-center italic">
                              Continues for {results.recoveryPeriod} years total
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Warnings */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
              <p className="text-xs font-semibold text-amber-800 mb-0.5">Bonus Depreciation</p>
              <p className="text-xs text-amber-700">Bonus depreciation rules changed after 2022. Consult a CPA to determine current eligibility.</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-3">
              <p className="text-xs font-semibold text-red-800 mb-0.5">Depreciation Recapture</p>
              <p className="text-xs text-red-700">When you sell, all depreciation claimed is subject to 25% recapture tax. Plan accordingly.</p>
            </div>
          </div>
        </div>
      </ToolShell>

      <FormulaBox
        formula={`Depreciable Basis = Purchase Price − Land Value + Improvements\nAnnual Depreciation = Depreciable Basis ÷ Recovery Period\n\nFirst Year (mid-month convention):\nMonths in Service = (13 − Month Placed) − 0.5\nFirst Year Depreciation = Annual × (Months in Service ÷ 12)\n\nAnnual Tax Savings = Annual Depreciation × Marginal Tax Rate`}
        description="The IRS mid-month convention assumes all property is placed in service at mid-month. Residential rental property uses a 27.5-year recovery period; commercial uses 39 years."
      />

      <ToolFAQ
        items={[
          {
            q: "Why can't I depreciate land?",
            a: "Land is considered to have an indefinite useful life and does not wear out, decay, or become obsolete. Only the structures built on land — and any improvements — qualify for depreciation deductions.",
          },
          {
            q: "What is the mid-month convention?",
            a: "IRS rules require residential and commercial rental property to use the mid-month convention. This means that regardless of which day in the month you place the property in service, it is treated as if it was placed in service at the midpoint of that month.",
          },
          {
            q: "What happens to my depreciation deductions when I sell?",
            a: "Depreciation recapture applies. The IRS taxes all previously claimed depreciation at a special recapture rate of 25%, regardless of your normal capital gains rate. This is a major factor in calculating net proceeds from a sale.",
          },
        ]}
      />

      <CTABox
        icon={AreaChart}
        heading="Categorize expenses for tax time"
        body={tool.ctaText}
        ctaLabel="Start free"
        ctaHref="/register"
      />

      <RelatedToolsList relatedSlugs={tool.related} />
    </div>
  )
}
